import React from "react";

import { fetchEntitlements, updateEntitlement } from "../api/accessAdminClient";

export function EntitlementsSettingsScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tenantId, setTenantId] = React.useState("");
  const [entitlements, setEntitlements] = React.useState<{ key: string; enabled: boolean }[]>([]);

  const loadEntitlements = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchEntitlements(tenantId || undefined);
      setEntitlements(response.entitlements ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load entitlements");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => {
    void loadEntitlements();
  }, [loadEntitlements]);

  const handleToggle = async (key: string, enabled: boolean) => {
    const reason = window.prompt("Reason for entitlement change") ?? "";
    if (!reason.trim()) return;
    try {
      await updateEntitlement(key, enabled, reason, tenantId || undefined);
      await loadEntitlements();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update entitlement");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Loading entitlements…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Entitlements</h2>
        <p className="text-sm text-slate-600">
          Platform admins can toggle product entitlements per tenant.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            className="input"
            placeholder="Tenant ID"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
          />
          <button type="button" className="button button-secondary" onClick={loadEntitlements}>
            Load
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-3">
          {entitlements.map((entitlement) => (
            <div key={entitlement.key} className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{entitlement.key}</div>
                <div className="text-xs text-slate-500">
                  {entitlement.enabled ? "Enabled" : "Disabled"}
                </div>
              </div>
              <button
                type="button"
                className={`button ${entitlement.enabled ? "button-primary" : "button-secondary"}`}
                onClick={() => handleToggle(entitlement.key, !entitlement.enabled)}
              >
                {entitlement.enabled ? "Disable" : "Enable"}
              </button>
            </div>
          ))}
          {entitlements.length === 0 ? (
            <div className="text-sm text-slate-500">No entitlements found.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
